import React, { useEffect, useState } from 'react';
import ProductCardProps from './types/ProductCardProps';
import Tag from '../Tag/Tag';
import clsx from 'clsx';
import ProductPrice from '../ProductPrice/ProductPrice';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import BuySampleButton from '../BuyButton/BuySampleButton';
import ProductCardImageSlider from './ProductCardImageSlider';
import ProductQuickViewModal from '../ProductQuickView/ProductQuickViewModal';
import useIsMobile from '../../Hooks/useIsMobile';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import useIsStoreScreen from '../../Hooks/useIsStoreScreen';
import { useTypedSelector } from '../../Hooks/useTypedSelector';

const ProductCard = ({
  product,
  lazyLoadImage,
  className,
  showInfo = true,
  showRoomshot = false,
  showPatternImage = false,
  disableImageSlider = false,
}: ProductCardProps) => {
  const quicksearchState = useTypedSelector((state) => state.quickSearch);
  const [renderQuickViewModal, setRenderQuickViewModal] = useState(false);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const isMobile = useIsMobile();

  const isStoreScreen = useIsStoreScreen();
  const canOpenModal =
    isStoreScreen || (isMobile && !product.hideModalOnMobile);
  // Set quick view model to open 1ms after we render it, to enable sliding in
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowQuickViewModal(renderQuickViewModal);
    }, 1);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [renderQuickViewModal]);

  return (
    <>
      <div
        className={clsx(
          'flex',
          'flex-col',
          'bg-white',
          'rounded',
          'h-full',
          'max-w-full',
          'm-w-0',
          'group',
          className
        )}
      >
        <div className="image-wrapper relative h-full rounded">
          {canOpenModal ? (
            <div
              className="flex h-full w-full overflow-hidden rounded bg-gray-200 aspect-[3/4]"
              onClick={() => setRenderQuickViewModal(true)}
            >
              {product.images && product.images.length > 0 && (
                <ProductCardImageSlider
                  images={product.images}
                  roomshotImage={product.roomshotImage}
                  patternImage={product.patternImage}
                  productUrl={product.url}
                  forceRoomshotImage={showRoomshot}
                  forcePatternImage={showPatternImage}
                  disableSlider={disableImageSlider}
                />
              )}
              {product.tags?.length ? (
                <div className="absolute left-2 top-2 flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <Tag tagSize="small" key={tag.id} {...tag}>
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <a
              href={product.url}
              className="flex h-full w-full overflow-hidden rounded bg-gray-200 aspect-[3/4]"
            >
              {product.images && product.images.length > 0 && (
                <ProductCardImageSlider
                  images={product.images}
                  roomshotImage={product.roomshotImage}
                  patternImage={product.patternImage}
                  productUrl={product.url}
                  forceRoomshotImage={showRoomshot}
                  forcePatternImage={showPatternImage}
                  disableSlider={disableImageSlider}
                />
              )}
              {product.tags?.length ? (
                <div className="absolute left-2 top-2 flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <Tag tagSize="small" key={tag.id} {...tag}>
                      {tag.name}
                    </Tag>
                  ))}
                </div>
              ) : null}
            </a>
          )}
          <FavoriteButton product={product} />
          {canOpenModal && (
            <button
              onClick={() => setRenderQuickViewModal(true)}
              className="absolute bottom-0 right-0 p-2"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-opacity-white-90 lg:hidden store-screen:lg:flex">
                <Icon name="plus" className="h-4 w-4" />
              </div>
            </button>
          )}
          <div className="absolute -bottom-2 left-0 right-0 hidden !h-[unset] px-2 pb-2 opacity-0 transition-all duration-500 group-hover:pointer-events-auto group-hover:bottom-0 group-hover:opacity-100 lg:flex">
            {!product.disableBuyOption &&
              !quicksearchState.disableBuyOption &&
              product.samples?.map((sample) => (
                <BuySampleButton
                  {...sample}
                  className="btn--large disabled:!opacity-100"
                  key={`sample-${sample.articleNumber}`}
                />
              ))}
          </div>
        </div>
        {showInfo && (
          <div className="flex flex-1 flex-col px-1 pt-2 lg:pt-3">
            <a
              className="lg:h4 h6 mb-0.5 block overflow-hidden text-ellipsis whitespace-nowrap font-normal"
              href={product.url}
            >
              {product.name}
            </a>
            {product.variantsAmount && product.variantsAmount > 0 && (
              <span className="mb-1 text-xs leading-tight text-gray-500">
                {product.variantsAmount}{' '}
                {product.variantsAmount == 1
                  ? translate('product-card.color')
                  : translate('product-card.colors')}
                {product.extraInfo?.items.length > 0 &&
                  product.extraInfo?.items.map((item, idx) => (
                    <span key={idx}>, {item.value}</span>
                  ))}
              </span>
            )}
            {product.price && <ProductPrice priceModel={product.price} />}
          </div>
        )}
      </div>
      {canOpenModal && renderQuickViewModal && (
        <ProductQuickViewModal
          product={product}
          open={showQuickViewModal}
          disableBuyOption={quicksearchState.disableBuyOption}
          onClosed={() => setRenderQuickViewModal(false)}
        />
      )}
    </>
  );
};

export default ProductCard;
